import { Link } from "react-router-dom";
import { ArrowRight, Compass, Users, Wallet, TrendingUp, Map } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import TrustFactsStrip from "@/components/books/TrustFactsStrip";
import TrustReviews from "@/components/books/TrustReviews";
import {
  LEVEL3_BOOKS,
  LEVEL3_BUNDLE,
  LEVEL3_SERIES,
  LEVEL3_WHY_DIFFERENT,
  LEVEL3_AUTHOR,
  LEVEL3_ACCENT as L,
} from "@/data/level3Books";
import { CHINNU_AUTHOR } from "@/data/books";

const STEP_ICONS = [Compass, Users, Wallet, TrendingUp, Map];

const LEVEL3_FACTS = [
  "LifeIQ Publications",
  "5 books",
  "489 PDF pages",
  "For teens & young adults",
  "Real situations",
  "Practical tools",
];

export default function Level3Series() {
  useTrackPageView("level3_series_view");
  useSEO({
    title: "LifeIQ Level 3 · Life Design | Real-World Life Skills for Teens & Young Adults",
    description: "Five practical self-read books for teens and young adults — self-management, communication, everyday money, long-term financial thinking, career direction, relationships, health and life design.",
    canonicalPath: "/books/level3",
  });

  return (
    <main>
      {/* SERIES HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: L.navy }}>
        <span aria-hidden="true" className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${L.gold}1E` }} />
        <span aria-hidden="true" className="absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${L.teal}1E` }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.gold }}>LifeIQ</span>
                  <span className="text-white/30">·</span>
                  <span className="text-[12px] uppercase tracking-[0.24em] text-white/60 font-semibold">Real-World Life Skills for Teens</span>
                </div>
              </HeroFade>
              <HeroFade delay={0.12}>
                <div className="mt-5 text-[13px] uppercase tracking-[0.2em] font-bold" style={{ color: L.teal }}>{LEVEL3_SERIES.name}</div>
              </HeroFade>
              <KineticTitle
                testId="level3-series-hero-title"
                className="mt-4 font-serif-d text-[36px] sm:text-5xl lg:text-[58px] leading-[1.06] tracking-tight text-white"
                lines={LEVEL3_SERIES.headline}
              />
              <HeroFade delay={0.28}>
                <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] px-3 py-1.5 rounded-sm" style={{ color: L.gold, backgroundColor: `${L.gold}1A` }}>
                  {LEVEL3_SERIES.audience}
                </div>
                <div className="mt-5 flex items-center gap-1.5 flex-wrap" data-testid="level3-progression-chips">
                  {LEVEL3_SERIES.progression.map((step, i) => (
                    <span key={step} className="flex items-center gap-1.5">
                      <span className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.08em] px-2.5 py-1.5 rounded-sm border" style={{ color: "white", borderColor: "rgba(255,255,255,0.22)" }}>
                        {step}
                      </span>
                      {i < LEVEL3_SERIES.progression.length - 1 && <ArrowRight size={12} className="text-white/35" />}
                    </span>
                  ))}
                </div>
                <p data-testid="level3-hero-intro" className="mt-6 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-xl">{LEVEL3_SERIES.intro}</p>
              </HeroFade>
              <HeroFade delay={0.42}>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#the-journey"
                    data-testid="level3-explore-books-cta"
                    className="inline-flex items-center justify-center gap-2 text-[13px] font-semibold tracking-wide uppercase px-7 py-4 rounded-sm hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: L.gold, color: L.navy }}
                  >
                    Explore the Five Books <ArrowRight size={15} />
                  </a>
                  <Link
                    to="/books/level3/complete-series"
                    data-testid="level3-complete-series-cta"
                    onClick={() => track("level3_complete_series_click", { source: "series-hero" })}
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  >
                    See the Complete Series <ArrowRight size={15} />
                  </Link>
                </div>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <TrustFactsStrip
                    facts={LEVEL3_FACTS}
                    accentColor={L.gold}
                    textColor="rgba(255,255,255,0.65)"
                    dividerColor="rgba(255,255,255,0.25)"
                    testId="level3-series-trust-facts"
                  />
                </div>
              </HeroFade>
            </div>

            {/* Editorial multi-cover composition — all 5 real covers, Book 5 forward */}
            <div className="lg:col-span-5">
              <HeroFade delay={0.3} className="relative h-[420px] sm:h-[460px] hidden sm:block">
                {LEVEL3_BOOKS.map((b, i) => (
                  <div
                    key={b.slug}
                    className="absolute"
                    style={{
                      width: i === 4 ? "40%" : "27%",
                      left: `${i * 15}%`,
                      top: `${(i % 2) * 12 + 4}%`,
                      zIndex: i,
                    }}
                  >
                    <BookCover book={b} tilt testId={`level3-hero-cover-${b.slug}`} />
                  </div>
                ))}
              </HeroFade>
              <div className="grid grid-cols-3 gap-3 sm:hidden">
                {[LEVEL3_BOOKS[0], LEVEL3_BOOKS[2], LEVEL3_BOOKS[4]].map((b) => (
                  <BookCover key={b.slug} book={b} testId={`level3-hero-cover-${b.slug}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE LEVEL 3 JOURNEY */}
      <section id="the-journey" style={{ backgroundColor: L.cream }}>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.gold }}>The Level 3 journey</div>
            <h2 className="mt-4 font-serif-d text-[30px] sm:text-4xl lg:text-[44px] leading-[1.1]" style={{ color: L.navy }}>Five books, one working system.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="level3-journey-grid">
            {LEVEL3_BOOKS.map((b, i) => {
              const StepIcon = STEP_ICONS[i];
              return (
                <Reveal key={b.slug} delay={i * 0.07}>
                  <div data-testid={`level3-journey-book-${b.book_number}`} className="group h-full bg-white border rounded-sm p-4 flex flex-col" style={{ borderColor: `${L.navy}14` }}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold" style={{ color: b.accent }}>{String(i + 1).padStart(2, "0")}</span>
                      <StepIcon size={15} style={{ color: b.accent }} />
                    </div>
                    <div className="mt-1.5 text-[11px] uppercase tracking-[0.1em] font-bold" style={{ color: b.accent }}>{b.series_step}</div>
                    <Link to={`/books/level3/${b.slug}`} className="mt-3 block max-w-[150px] mx-auto">
                      <BookCover book={b} className="group-hover:scale-[1.02] transition-transform duration-300" />
                    </Link>
                    <Link to={`/books/level3/${b.slug}`} className="mt-3 font-serif-d text-[16px] leading-[1.25] hover:underline" style={{ color: L.navy }}>{b.title}</Link>
                    <p className="mt-1 text-[11px] font-semibold" style={{ color: L.textMuted }}>{b.subtitle}</p>
                    <p className="mt-2 text-[12px] leading-[1.5] flex-1 line-clamp-3" style={{ color: L.textMuted }}>{b.short_description}</p>
                    <div className="mt-2 text-[11px] font-semibold" style={{ color: L.textMuted }}>{b.pages} pages · {b.tools_count} practical tools</div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-[12px] line-through" style={{ color: L.textMuted }}>{b.regular_price}</span>
                      <span className="text-[16px] font-bold" style={{ color: b.accent }}>{b.price}</span>
                      <span className="text-[9px] uppercase tracking-[0.08em] font-bold" style={{ color: L.gold }}>{b.offer_label}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: `${L.navy}12` }}>
                      <Link to={`/books/level3/${b.slug}#look-inside`} data-testid={`level3-journey-look-inside-${b.slug}`} className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: b.accent }}>
                        Look Inside <ArrowRight size={11} />
                      </Link>
                      <Link to={`/books/level3/${b.slug}`} data-testid={`level3-journey-view-book-${b.slug}`} className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: L.navy }}>
                        View Book <ArrowRight size={11} />
                      </Link>
                    </div>
                    <Link
                      to={`/books/level3/${b.slug}`}
                      data-testid={`level3-journey-buy-${b.slug}`}
                      className="mt-2.5 inline-flex items-center justify-center gap-1.5 text-white py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-sm hover:opacity-90 transition-opacity duration-200"
                      style={{ backgroundColor: b.accent }}
                    >
                      Buy — {b.price} <ArrowRight size={11} />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-[14px] text-center max-w-2xl mx-auto" style={{ color: L.textMuted }}>{LEVEL3_SERIES.altIntro}</p>
          </Reveal>
        </div>
      </section>

      {/* COMPLETE SERIES BLOCK — strengthened; no price shown, pricing pending owner approval */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-24">
          <Reveal>
            <div className="rounded-sm overflow-hidden" style={{ backgroundColor: L.navy }}>
              <div className="px-6 sm:px-12 py-14 sm:py-16 text-center">
                <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.gold }}>The complete series</div>
                <h3 className="mt-4 font-serif-d text-[28px] sm:text-[38px] leading-[1.15] text-white">
                  The Complete<br />Level 3 · Life Design Series
                </h3>
                <div className="mt-8 grid grid-cols-5 gap-3 sm:gap-4 max-w-[520px] mx-auto" data-testid="level3-bundle-banner-covers">
                  {LEVEL3_BOOKS.map((b) => <BookCover key={b.slug} book={b} testId={`level3-bundle-banner-cover-${b.slug}`} />)}
                </div>
                <div className="mt-8 flex items-center justify-center gap-1.5 flex-wrap">
                  {LEVEL3_SERIES.progression.map((step, i) => (
                    <span key={step} className="flex items-center gap-1.5">
                      <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.07em] px-2 sm:px-2.5 py-1.5 rounded-sm border" style={{ color: "white", borderColor: "rgba(255,255,255,0.22)" }}>
                        {step}
                      </span>
                      {i < LEVEL3_SERIES.progression.length - 1 && <ArrowRight size={10} className="text-white/35" />}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-[13px] sm:text-[14px] uppercase tracking-[0.12em] font-bold text-white/70">
                  {LEVEL3_BUNDLE.totalBooks} Digital Books · {LEVEL3_BUNDLE.totalPages} PDF Pages
                </p>
                <p className="mt-4 text-[15px] sm:text-[16px] text-white/80 leading-[1.65] max-w-lg mx-auto">{LEVEL3_BUNDLE.description}</p>
                <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
                  <span className="text-[16px] sm:text-[18px] line-through text-white/45">{LEVEL3_BUNDLE.regular_price}</span>
                  <span className="text-[32px] sm:text-[38px] font-serif-d font-bold" style={{ color: L.gold }}>{LEVEL3_BUNDLE.price}</span>
                </div>
                <div className="mt-2 text-[12px] font-bold uppercase tracking-[0.14em]" style={{ color: L.gold }}>
                  {LEVEL3_BUNDLE.offer_label} · Save {LEVEL3_BUNDLE.savings}
                </div>
                <Link
                  to="/books/level3/complete-series"
                  data-testid="level3-bundle-banner-cta"
                  onClick={() => track("level3_complete_series_click", { source: "series-banner" })}
                  className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold tracking-wide uppercase px-7 py-4 rounded-sm hover:opacity-90 transition-opacity duration-200"
                  style={{ backgroundColor: L.gold, color: L.navy }}
                >
                  Get All 5 Books — {LEVEL3_BUNDLE.price} <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY LEVEL 3 IS DIFFERENT */}
      <section style={{ backgroundColor: L.cream }}>
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.teal }}>Why Level 3 is different</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: L.navy }}>Built for real life.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="level3-why-different-grid">
            {LEVEL3_WHY_DIFFERENT.map((idea, i) => (
              <Reveal key={idea.n} delay={i * 0.06}>
                <div className="flex gap-4">
                  <div className="font-serif-d text-[28px] leading-none shrink-0" style={{ color: L.gold }}>{idea.n}</div>
                  <div>
                    <div className="font-serif-d text-[17px]" style={{ color: L.navy }}>{idea.title}</div>
                    <p className="mt-1.5 text-[14px] leading-[1.6]" style={{ color: L.textMuted }}>{idea.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / REVIEWS — prepared for future use, disabled until owner
          supplies genuine, consented reviewer/media content. */}
      <TrustReviews items={[]} enabled={false} accentColor={L.gold} navyColor={L.navy} mutedColor={L.textMuted} />

      {/* ABOUT THE AUTHOR — reuses the same owner-approved photo already
          used on /books/chinnu; no new image generated. */}
      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-8 items-start">
              <img src={CHINNU_AUTHOR.photo} alt={CHINNU_AUTHOR.photo_alt} className="w-full max-w-[160px] rounded-sm" data-testid="level3-author-photo" />
              <div>
                <div className="text-[12px] uppercase tracking-[0.2em] font-semibold" style={{ color: L.gold }}>About the author</div>
                <h3 className="mt-2 font-serif-d text-[22px]" style={{ color: L.navy }}>{LEVEL3_AUTHOR.name}</h3>
                <p className="mt-1 text-[13px] font-semibold" style={{ color: L.teal }}>{LEVEL3_AUTHOR.publisher} — {LEVEL3_AUTHOR.publisherNote}</p>
                <p className="mt-4 text-[14px] leading-[1.7]" style={{ color: L.textMuted }}>
                  Kunti Sudheer Bose writes LifeIQ's real-world life-skills books — practical, self-read guides built around the situations teens and young adults actually encounter, from handling emotion and people to money, growth and life design.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
